import { z } from "zod";
//#region src/isolated-pool-pipe.ts
/** ⛔ Bump ONLY for a breaking wire change. The runner stamps it in `ZION_POOL_PROTOCOL` and the
*  pod echoes it in /healthz; a mismatch is a 400 `unsupported-protocol`, never a best-effort
*  attempt — a silently-mismatched protocol is how a "successful" job returns the wrong shape.
*
*  **v2 (2026-08-22, WS-5):** {@link isolatedJobSuccessSchema} became a two-arm union — a terminal
*  frame may now report an OVERSIZE result instead of carrying it — and {@link
*  ISOLATED_POOL_RESULT_PATH} was added to collect those bytes. Both are breaking: a v1 runner
*  handed a v2 oversize frame would read `output` as `undefined` and report success with no result.
*
*  ⚠️ THE BUMP IS ONLY FREE BECAUSE THERE ARE NO LIVE PODS. Runner and pod images roll
*  independently, so a v2 runner meeting a v1 pod is a hard `/healthz` mismatch. That is correct
*  behaviour and it reads as `pod-start-failed`. ⇒ Ship runner and pod in ONE DP release; after the
*  canary adopts this, a bump needs a negotiation, not an increment. */
const ISOLATED_POOL_PROTOCOL_VERSION = 2;
const ISOLATED_POOL_HEALTH_PATH = "/healthz";
const ISOLATED_POOL_RUN_PATH = "/v1/run";
const ISOLATED_POOL_RUN_STREAM_PATH = "/v1/run/stream";
/**
* Re-attach to an attempt this pod is running or has finished (zion-infra#2619). POST
* `isolatedJobAttachRequestSchema`; the answer is the SSE body `/v1/run/stream` writes. For the
* running job with this `jobId`: `accepted`, then the frames from now on. The previous reader is
* abandoned and nothing is replayed. For a finished job whose terminal frame the pod retained
* (`ISOLATED_POOL_RETAINED_TERMINALS_MAX`): `accepted`, that `result` or `error` frame, the end.
* The entry is cleared before the write, so it is delivered once. Otherwise `404 unknown-job`.
*
* Additive on purpose. A pod built before this door answers `404 bad-request` ("no such path"),
* and the runner reads that as "this pod predates attach" and leaves the row to its reaper. The
* protocol version stays 2: a bump would force runner and pod into one release, and the pod side
* ships inside every app's image.
*/
const ISOLATED_POOL_RUN_ATTACH_PATH = "/v1/run/attach";
/**
* Collect an OVERSIZE result's raw bytes. GET, no body, **exactly once per job**.
*
* ⛔ A SECOND ROUTE RATHER THAN A BIGGER TERMINAL FRAME, AND THE REASON IS A MEASUREMENT.
* Letting the terminal frame grow to the artifact ceiling forces the runner to hold it: the
* operator's memory LIMIT is 512Mi and `maxConcurrentRuns` defaults to 8, so `8 × 64 MiB` is the
* entire limit before the operator's own working set — an OOMKill a tenant can trigger on demand.
* The only way to avoid buffering inside a JSON envelope is to parse the envelope incrementally,
* i.e. to run a streaming JSON parser over untrusted multi-megabyte input inside the operator.
* ⇒ A separate route makes the payload **opaque bytes the runner never parses**: it pipes them
* straight to blob storage in constant memory. Smaller AND safer than the simpler-looking option.
*
* Served ONLY after a `/v1/run`(-stream) that produced an oversize terminal frame; a call in any
* other state is `500` `internal`. The response is `application/json` with `content-length` set,
* carrying the raw serialised handler output and **nothing else** — no envelope, no wrapper.
*/
const ISOLATED_POOL_RESULT_PATH = "/v1/result";
/** 1 MiB, INBOUND. The compute-unit broker's 16 KiB cap is too small for job args; the
*  READER PATTERN is the same one (`readJsonBody` in compute-unit-broker-server.ts): a byte-capped
*  DRAIN, never a destroy — destroying mid-body makes the peer see a connection reset instead of
*  the 413 that tells it what happened.
*
*  ⛔ UNTOUCHED BY WS-5, AND IT IS THE NEIGHBOURING CONSTANT ONE CHARACTER DIFFERENT IN THE
*  READER'S MEMORY. This bounds the pod's inbound BODY (`args`), which stays a hard cap because a
*  large input is PULL-shaped and a sealed pod cannot fetch — the mirror mechanism cannot exist.
*  Only the RESULT direction gained a spill. */
const ISOLATED_POOL_MAX_REQUEST_BYTES = 1048576;
/**
* 1 MiB. ⛔⛔ **A THRESHOLD, NOT A CAP — this changed meaning in protocol v2 and the value did not.**
*
* | | before v2 | now |
* |---|---|---|
* | meaning | the largest result that may EXIST | the largest result carried INLINE |
* | exceeding it | terminal, permanent failure | the POINTER representation |
*
* ⛔ IT IS NOT CONFIGURABLE, AND THAT IS DELIBERATE. Three parties in three different processes
* must agree on it: the pod (may put at most this many bytes in a terminal frame), the runner (its
* byte-capped drain on the terminal frame) and the run store (refuses a `WorkResult` serialising
* above it). A per-DP knob makes the three disagree the moment one is redeployed without the
* others. This decides WHICH OF TWO REPRESENTATIONS a value takes — that is a contract boundary,
* and a contract boundary that varies per deployment is not a contract.
*
* ⇒ For "how large may a result be at all", see {@link POOL_DEFAULT_MAX_ARTIFACT_BYTES}, which is
* a RESOURCE question and therefore IS configurable. ⛔ Never harmonise the two.
*/
const ISOLATED_POOL_MAX_RESULT_BYTES = 1048576;
/**
* The absolute ceiling on ONE run's spilled artifact, in bytes, before configuration.
*
* ⛔ NOT THE SAME QUESTION AS {@link ISOLATED_POOL_MAX_RESULT_BYTES}, and the two must never be
* "harmonised". That one decides which REPRESENTATION a result takes and is a contract three
* processes must agree on. This one decides how much a single run may COST — storage, transfer
* time, operator bandwidth — which legitimately differs between a starter DP and an enterprise
* one, so it is per-DP configurable via `POOL_MAX_ARTIFACT_BYTES`.
*
* ⭐ The value EQUALS `CLAIM_CHECK_DEFAULT_MAX_OFFLOAD_BYTES` on purpose: the platform should have
* ONE answer to *"how large may an offloaded payload be"*, not two. It is deliberately **not
* imported** from there — the two mechanisms are unrelated and coupling their module graphs to
* express agreement would cost more than the agreement is worth. `IP-C1` asserts the equality
* instead, so a deliberate divergence is a visible edit and an accidental one is a red test. Same
* two-encodings-one-fact discipline `WORK_POOL_NAME_MAX_LENGTH` already uses against the `{0,38}`
* inside `WORK_POOL_NAME_PATTERN`.
*
* ⛔ A configured ceiling BELOW the inline threshold is refused at BOOT, not clamped: it would make
* the pointer arm unreachable and silently restore the exact defect WS-5 exists to remove. A
* configuration that disables a feature must fail loudly.
*/
const POOL_DEFAULT_MAX_ARTIFACT_BYTES = 67108864;
const isolatedPoolHealthSchema = z.object({
	ready: z.literal(true),
	protocol: z.number().int().positive(),
	jobId: z.string()
}).strict();
const isolatedJobRequestSchema = z.object({
	protocol: z.number().int().positive(),
	jobId: z.string().min(1),
	/** Opaque to the platform. The pod's handler owns the shape; the runner never inspects it.
	*  `z.unknown()` rather than a record so a scalar or an array is legal too. */
	args: z.unknown(),
	/** Wall-clock milliseconds the handler may take, measured from the moment the pod accepts
	*  the request. Always <= the pod's own `ZION_POOL_DEADLINE_SECONDS`. */
	deadlineMs: z.number().int().positive()
}).strict();
/** The attach body (zion-infra#2619): the protocol, and the attempt id the original run request
*  carried as `jobId`. The pod compares it and never interpolates it. */
const isolatedJobAttachRequestSchema = z.object({
	protocol: z.number().int().positive(),
	jobId: z.string().min(1)
}).strict();
/** How many finished attempts' terminal frames a pod keeps for a late attach (zion-infra#2619),
*  oldest evicted. The pod retains an inline `result` and every `error`. An oversize `result` with
*  no reader stays dropped: its bytes sit in the one un-keyed artifact slot a later attempt
*  overwrites (R-023). A per-attempt pod holds one. A standing member that finished attempts
*  through a whole operator roll holds up to this many. Worst case per pod: this many times
*  `ISOLATED_POOL_MAX_RESULT_BYTES`, 16 MiB. The pod drops an entry older than the TTL below at
*  its next insert or read (R-053), so the age bound holds at those moments and the memory bound
*  is the count. Delivered once each; a collected entry is gone. */
const ISOLATED_POOL_RETAINED_TERMINALS_MAX = 16;
/** A retained terminal expires after this, and an expired id answers `unknown-job`. Ten minutes
*  covers the measured roll (the old operator pod deleted at 18:18:15Z, the new one listening at
*  18:20Z, zion-infra#2619) plus the new operator's first cycle, bounded by its 120 s watchdog,
*  with headroom (R-023). */
const ISOLATED_POOL_RETAINED_TERMINAL_TTL_MS = 6e5;
const ISOLATED_JOB_ERROR_CODES = [
	"bad-request",
	"unsupported-protocol",
	"wrong-job",
	"already-running",
	"job-failed",
	"job-timeout",
	"result-too-large",
	"internal",
	"caller-disconnected",
	"unknown-job"
];
const isolatedJobErrorSchema = z.object({
	ok: z.literal(false),
	code: z.enum(ISOLATED_JOB_ERROR_CODES),
	/** ⛔ Never interpolate the args or any handler payload into this string: it is logged by the
	*  runner and by the worker. Same rule as compute-unit-naming.ts's INV-CU-8. */
	message: z.string(),
	/** TRUE only for a condition a fresh pod would plausibly survive (`internal`). `job-failed`
	*  and `job-timeout` are FALSE: retrying deterministic tenant code burns a pod-start for
	*  nothing, and D4 means every retry is a new pod. */
	retriable: z.boolean(),
	durationMs: z.number().int().nonnegative()
}).strict();
/**
* The handler's output FITS INLINE (<= {@link ISOLATED_POOL_MAX_RESULT_BYTES}).
*
* ⭐ Byte-identical to the protocol-1 success frame — this arm is what an unchanged handler still
* produces, which is why the v2 bump costs pod AUTHORS nothing.
*/
const isolatedJobInlineSuccessSchema = z.object({
	ok: z.literal(true),
	output: z.unknown(),
	durationMs: z.number().int().nonnegative()
}).strict();
/**
* The handler's output EXCEEDS the inline threshold. The bytes are collected by the runner over
* {@link ISOLATED_POOL_RESULT_PATH}.
*
* ⛔ `output` IS ABSENT, NOT EMPTY. The two arms are mutually exclusive, and `.strict()` makes
* "both" **unrepresentable** rather than merely wrong — a frame carrying `output` AND `oversize`
* fails to parse. Had `output` been optional here, a pod could report an oversize result and also
* inline a value, and nothing would say which one the platform should believe.
*
* ⛔⛔ `bytes` AND `sha256` ARE THE POD'S DECLARATION, NOT FACT. The pod is untrusted code. The
* runner counts and hashes the bytes as they pass and fails the run on either mismatch; the digest
* recorded in the artifact ref is the RUNNER'S OWN. Nothing downstream may read these two fields
* as truth — they exist so the runner can refuse an over-ceiling transfer BEFORE opening it, and
* so a lying pod is detectable rather than merely bounded.
*/
const isolatedJobOversizeSuccessSchema = z.object({
	ok: z.literal(true),
	oversize: z.object({
		bytes: z.number().int().positive(),
		sha256: z.string().regex(/^[0-9a-f]{64}$/)
	}).strict(),
	durationMs: z.number().int().nonnegative()
}).strict();
/**
* ⛔ A UNION, AND EVERY CONSUMER MUST NARROW BEFORE READING `output`.
*
* ⚠️ This is the breaking half of protocol v2 and it is a COMPILE error at every read site, which
* is the point: `result.output` on the union does not type-check, so a consumer that ignored the
* oversize arm cannot silently report `undefined` as a successful empty result.
*/
const isolatedJobSuccessSchema = z.union([isolatedJobInlineSuccessSchema, isolatedJobOversizeSuccessSchema]);
const isolatedJobResultSchema = z.union([isolatedJobSuccessSchema, isolatedJobErrorSchema]);
const ISOLATED_JOB_EVENT_NAMES = [
	"accepted",
	"progress",
	"log",
	"result",
	"error"
];
const isolatedJobAcceptedEventSchema = z.object({
	protocol: z.number().int().positive(),
	jobId: z.string(),
	startedAtEpochMs: z.number().int().positive()
}).strict();
const isolatedJobProgressEventSchema = z.object({
	seq: z.number().int().positive(),
	message: z.string(),
	pct: z.number().int().min(0).max(100).optional()
}).strict();
const isolatedJobLogEventSchema = z.object({
	seq: z.number().int().positive(),
	stream: z.enum(["stdout", "stderr"]),
	chunk: z.string()
}).strict();
/** Emitted as an SSE COMMENT (`: ka\n\n`), not an event — a comment cannot be mistaken for a
*  protocol event by a lenient parser, and it resets every idle timer on the path. */
const ISOLATED_POOL_SSE_KEEPALIVE_MS = 15e3;
/** Runner-side. How long to poll GET /healthz before giving up on the pod ever starting.
*  Covers image pull on a warm node (D4 — the node is warm, the image may not be cached). A COLD
*  node's pull is {@link APP_POOL_IMAGE_PULL_TIMEOUT_MS}'s, and this clock does not run while one
*  is in flight (#3010). */
const ISOLATED_POOL_READY_TIMEOUT_MS = 12e4;
const ISOLATED_POOL_READY_POLL_INTERVAL_MS = 500;
/**
* Runner-side. How long an app-pool attempt's pod may sit UNSCHEDULED, measured from pod creation,
* while the node group it selects is still adding a node.
*
* ⛔ A SECOND CLOCK, NOT A LONGER FIRST ONE. `ISOLATED_POOL_READY_TIMEOUT_MS` is sized for an image
* pull on a node the pod already holds, and under the two-clock rule it runs from the pod's
* `PodScheduled` transition. This one covers the node add that has to happen before there is a node
* to pull onto. One flat deadline over both would charge a slow image pull and a node add to the
* same budget: a burst that needed a new node would fail on a clock sized for the pull, and
* lengthening that clock would let a genuinely broken image sit for five minutes.
*
* ⭐ DERIVED, NOT CHOSEN. It is the operator's own `ISOLATED_POOL_STALL_THRESHOLD_MS`, the reading
* the isolated-pool stall detector already treats as "a node that has not arrived by now is not
* arriving". Measured against the two GKE facts on dp-app: a normal node add takes 60 s, so this
* clears the ordinary case five times over; a GCE stockout gives up after 35 minutes, so it frees
* the pool's concurrency slot a seventh of the way into the worst case rather than holding it for
* the run's whole deadline.
*
* ⛔ THE ATTEMPT'S REAL CEILING IS THE SUM OF THE THREE CLOCKS, 1020 s, AND NO CONSTANT STATES IT
* ALONE. The readiness clock starts once the pod is scheduled, so a pod the scheduler places at
* 299 s still gets a fresh `ISOLATED_POOL_READY_TIMEOUT_MS` after it, and
* {@link APP_POOL_IMAGE_PULL_TIMEOUT_MS} buys a pulling pod another 600 s on top (#3010). Anyone
* sizing a caller's own `timeoutMs`, or reading any one constant as "how long a run can wait for a
* pod", wants `APP_POOL_SCHEDULING_TIMEOUT_MS + APP_POOL_IMAGE_PULL_TIMEOUT_MS +
* ISOLATED_POOL_READY_TIMEOUT_MS`. The caller's own deadline still cuts the wait short of it: the
* operator's readiness poll honours the abort the dispatcher raises when the row's `timeoutMs`
* passes.
*/
const APP_POOL_SCHEDULING_TIMEOUT_MS = 3e5;
/**
* Runner-side. How long an app-pool attempt's pod may spend PULLING a container image, measured
* from the kubelet's own `Pulling` event for the container that is pulling (zion-infra#3010).
*
* ⛔ A THIRD CLOCK, AND WHILE IT RUNS THE READINESS CLOCK DOES NOT. `ISOLATED_POOL_READY_TIMEOUT_MS`
* is sized for an image pull on a node that already holds the image, and it says so. A cold node
* has to fetch the bytes first: on dp-app on 2026-09-22 an mcp-catalog attempt pulled an 873 MiB
* worker image in 75 s, starting 84 s after the pod was scheduled because the pod's three sidecars
* pull first and the kubelet pulls sequentially. Charged to the readiness clock that is 159 s of a
* 120 s budget, so the attempt died `pod-start-failed`, the dispatcher retried it onto another cold
* node, and it died the same way. Widening the readiness clock instead would charge one budget for
* two independent waits, which is the thing #2663 split apart.
*
* ⭐ 600 s, AND THE SIZE IS THE PULL'S OWN WORST CASE RATHER THAN A MARGIN ON THE MEASURED ONE. The
* measured pull moved 914915592 bytes in 75.5 s. The same bytes at 15 Mbit/s, the floor a node
* competing with every other pod on its NIC can reach, take 490 s. This clears that and still
* fails a pull the registry will never serve five times sooner than the run's own
* `maxDurationSeconds` would.
*
* ⛔ IT DOES NOT REPLACE THE BROKEN-PULL ARM. A pull the registry refuses reads `ImagePullBackOff`
* or `ErrImagePull` on the container's own status, and the runner ends the attempt once that status
* has held for fifteen seconds rather than spending this bound on a reference that will never
* resolve. This clock is for a pull that is WORKING and slow.
*
* ⛔ IT IS A TOTAL FOR THE WHOLE ATTEMPT AND NOT A BUDGET PER CONTAINER. The kubelet pulls
* sequentially and a pod carries its sidecars, so the measured four-container pod would reach
* 2820 s if each pull were clocked from its own `Pulling` event. The runner measures the time the
* kubelet spent fetching, completed pulls included, against this clock, and everything else against
* the readiness clock, so the two totals sum to the time since the pod was scheduled.
*
* ⛔ AND THE BOUND ENDS AN ATTEMPT ONLY WHILE A PULL IS STILL RUNNING. A pod that FINISHES its last
* pull has its bytes on the node and its containers starting, so it keeps its full
* `ISOLATED_POOL_READY_TIMEOUT_MS` however long the fetching took; killing it there would discard
* the whole pull to reclaim a fraction of it. Such an attempt fails on the readiness clock instead,
* and its message names the pull time when the pull was at least half the wait, so the reader is
* not sent to the entrypoint for a pod whose wall clock was mostly fetching.
*
* ⛔ THE ATTEMPT'S CEILING IS THEREFORE THE SUM OF THE THREE CLOCKS, 1020 s, WHATEVER THE POD'S
* CONTAINER COUNT, AND NO CONSTANT STATES IT ALONE. Anyone sizing a caller's own `timeoutMs` wants
* `APP_POOL_SCHEDULING_TIMEOUT_MS + APP_POOL_IMAGE_PULL_TIMEOUT_MS + ISOLATED_POOL_READY_TIMEOUT_MS`.
* The caller's own deadline still cuts the wait short of it.
*/
const APP_POOL_IMAGE_PULL_TIMEOUT_MS = 6e5;
/** Runner-side. Abort the stream if no bytes arrive for this long. Keepalives are 15 s, so a
*  healthy stream refreshes this four times over. */
const ISOLATED_POOL_STREAM_IDLE_TIMEOUT_MS = 6e4;
/** ⛔ THE HANDLER→SERVER MARGIN, STATED. The nesting below only works if each layer is STRICTLY
*  outside the last; EQUAL values are a race, and the race loses exactly the property the nesting
*  exists to buy — the pod-side server and the handler time out together, nobody emits
*  `job-timeout`, and the runner observes an unexplained disappearance instead of a reported
*  timeout. One second is ample: the only work between the two is emitting one SSE frame. */
const ISOLATED_POOL_HANDLER_DEADLINE_MARGIN_MS = 1e3;
//#endregion
export { APP_POOL_IMAGE_PULL_TIMEOUT_MS, APP_POOL_SCHEDULING_TIMEOUT_MS, ISOLATED_JOB_ERROR_CODES, ISOLATED_JOB_EVENT_NAMES, ISOLATED_POOL_HANDLER_DEADLINE_MARGIN_MS, ISOLATED_POOL_HEALTH_PATH, ISOLATED_POOL_MAX_REQUEST_BYTES, ISOLATED_POOL_MAX_RESULT_BYTES, ISOLATED_POOL_PROTOCOL_VERSION, ISOLATED_POOL_READY_POLL_INTERVAL_MS, ISOLATED_POOL_READY_TIMEOUT_MS, ISOLATED_POOL_RESULT_PATH, ISOLATED_POOL_RETAINED_TERMINALS_MAX, ISOLATED_POOL_RETAINED_TERMINAL_TTL_MS, ISOLATED_POOL_RUN_ATTACH_PATH, ISOLATED_POOL_RUN_PATH, ISOLATED_POOL_RUN_STREAM_PATH, ISOLATED_POOL_SSE_KEEPALIVE_MS, ISOLATED_POOL_STREAM_IDLE_TIMEOUT_MS, POOL_DEFAULT_MAX_ARTIFACT_BYTES, isolatedJobAcceptedEventSchema, isolatedJobAttachRequestSchema, isolatedJobErrorSchema, isolatedJobInlineSuccessSchema, isolatedJobLogEventSchema, isolatedJobOversizeSuccessSchema, isolatedJobProgressEventSchema, isolatedJobRequestSchema, isolatedJobResultSchema, isolatedJobSuccessSchema, isolatedPoolHealthSchema };

//# sourceMappingURL=isolated-pool-pipe.js.map